import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ButtonComponent from '../../../components/ButtonComponent';
import BreadCrumbs from '../../../components/BreadCrumbs';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { 
  useUpdateRestaurantHttpsMutation 
} from '../../../redux/services/restaurantServices';
import { useRestaurantDetails } from '../../../hooks/useFirebaseOrders';

interface TimeSlot {
  openTime: string;
  closeTime: string;
  timeRange: string;
}

interface DeliveryMealTimeSlots {
  breakfast: TimeSlot;
  lunch: TimeSlot;
  dinner: TimeSlot;
}

interface OrderTimesData {
  deliveryTimeSlot: DeliveryMealTimeSlots;
  deliveryTimeSlotAdmin: TimeSlot;
  pickUpOrderClosingTime: string;
  deliveryOrderDelayTime: number;
  maxDaysToScheduleAhead: string;
  defaultOrderClosingTime: string;
  pickupTimeSlot: DeliveryMealTimeSlots;
  pickupTimeSlotAdmin: TimeSlot;
}

const OrderTimes = () => {
  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const [formData, setFormData] = useState<OrderTimesData>({
    deliveryTimeSlot: {
      breakfast: {
        openTime: '',
        closeTime: '',
        timeRange: '',
      },
      lunch: {
        openTime: '',
        closeTime: '',
        timeRange: '',
      },
      dinner: {
        openTime: '',
        closeTime: '',
        timeRange: '',
      },
    },
    deliveryTimeSlotAdmin: {
      openTime: '',
      closeTime: '',
      timeRange: '',
    },
    pickUpOrderClosingTime: '',
    deliveryOrderDelayTime: 0,
    maxDaysToScheduleAhead: '',
    defaultOrderClosingTime: '',
    pickupTimeSlot: {
      breakfast: {
        openTime: '',
        closeTime: '',
        timeRange: '',
      },
      lunch: {
        openTime: '',
        closeTime: '',
        timeRange: '',
      },
      dinner: {
        openTime: '',
        closeTime: '',
        timeRange: '',
      },
    },
    pickupTimeSlotAdmin: { openTime: '', closeTime: '', timeRange: '' },
  });

  const [isSaving, setIsSaving] = useState(false);
  const { data: restaurantData, loading } = useRestaurantDetails(selectedRestaurantId);
  const [updateRestaurant] = useUpdateRestaurantHttpsMutation();

  // Helper function to parse time slots array and extract openTime, closeTime, and timeRange
  const parseTimeSlots = (timeSlots: string[]): TimeSlot => {
    if (!timeSlots || timeSlots.length === 0) {
      return { openTime: '', closeTime: '', timeRange: '' };
    }

    // Get first and last time slot
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];

    // Extract times from "12:00 PM - 12:30 PM" format
    const firstSlotParts = firstSlot.split(' - ');
    const lastSlotParts = lastSlot.split(' - ');

    const openTime = convertTo24Hour(firstSlotParts[0].trim());
    const closeTime = convertTo24Hour(lastSlotParts[1].trim());

    // Calculate time range (difference in minutes)
    const firstSlotStart = convertTo24Hour(firstSlotParts[0].trim());
    const firstSlotEnd = convertTo24Hour(firstSlotParts[1].trim());
    const timeRange = calculateTimeDifference(firstSlotStart, firstSlotEnd);

    return { openTime, closeTime, timeRange: timeRange.toString() };
  };

  const time12ToMinutes = (time12h: string): number => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':').map(Number);

    if (hours === 12) {
      hours = 0;
    }

    if (modifier === 'PM') {
      hours += 12;
    }

    return hours * 60 + minutes;
  };

  const getSlotStartMinutes = (slot: string): number => {
    const [start] = slot.split(' - ');
    return time12ToMinutes(start.trim());
  };

  const splitDeliverySlotsByMeal = (timeSlots: string[]): DeliveryMealTimeSlots => {
    const breakfastSlots: string[] = [];
    const lunchSlots: string[] = [];
    const dinnerSlots: string[] = [];

    (timeSlots || []).forEach((slot) => {
      const startMinutes = getSlotStartMinutes(slot);

      if (startMinutes < 11 * 60) {
        breakfastSlots.push(slot);
      } else if (startMinutes < 16 * 60) {
        lunchSlots.push(slot);
      } else {
        dinnerSlots.push(slot);
      }
    });

    return {
      breakfast: parseTimeSlots(breakfastSlots),
      lunch: parseTimeSlots(lunchSlots),
      dinner: parseTimeSlots(dinnerSlots),
    };
  };

  // Convert 12-hour format to 24-hour format (HH:mm)
  const convertTo24Hour = (time12h: string): string => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }

    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours.padStart(2, '0')}:${minutes}`;
  };

  // Calculate time difference in minutes
  const calculateTimeDifference = (start: string, end: string): number => {
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    return endTotalMinutes - startTotalMinutes;
  };

  // Generate time slots array from openTime, closeTime, and timeRange
  const generateTimeSlots = (openTime: string, closeTime: string, timeRange: number): string[] => {
    if (!openTime || !closeTime || timeRange <= 0) {
      return [];
    }

    const slots: string[] = [];
    const [startHours, startMinutes] = openTime.split(':').map(Number);
    const [endHours, endMinutes] = closeTime.split(':').map(Number);

    let currentTime = startHours * 60 + startMinutes;
    const endTime = endHours * 60 + endMinutes;

    while (currentTime + timeRange <= endTime) {
      const slotStart = minutesToTime12Hour(currentTime);
      const slotEnd = minutesToTime12Hour(currentTime + timeRange);
      slots.push(`${slotStart} - ${slotEnd}`);
      currentTime += timeRange;
    }

    return slots;
  };

  const sortSlotsByStartTime = (slots: string[]): string[] => {
    return [...slots].sort((a, b) => getSlotStartMinutes(a) - getSlotStartMinutes(b));
  };

  const mergeDeliverySlots = (...slotGroups: string[][]): string[] => {
    const merged = slotGroups.flat();
    const uniqueSlots = Array.from(new Set(merged));
    return sortSlotsByStartTime(uniqueSlots);
  };

  // Convert minutes to 12-hour format
  const minutesToTime12Hour = (minutes: number): string => {
    let hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const modifier = hours >= 12 ? 'PM' : 'AM';

    if (hours > 12) {
      hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }

    return `${hours}:${mins.toString().padStart(2, '0')} ${modifier}`;
  };

  // useEffect(() => {
  //   if (selectedRestaurantId) {
  //     triggerGetRestaurant(selectedRestaurantId);
  //   }
  // }, [selectedRestaurantId, triggerGetRestaurant]);

  useEffect(() => {
    if (restaurantData) {
      const restaurant = restaurantData;
      
      // Parse time slots arrays
      const deliverySlot = splitDeliverySlotsByMeal(restaurant.deliveryTimeSlots || []);
      const deliverySlotAdmin = parseTimeSlots(restaurant.deliveryTimeSlotsAdmin || []);

      setFormData({
        deliveryTimeSlot: deliverySlot,
        deliveryTimeSlotAdmin: deliverySlotAdmin,
        pickUpOrderClosingTime: restaurant.pickupOrderClosingTime || '',
        deliveryOrderDelayTime: parseInt(restaurant.deliveryOrderDelayTime) || 0,
        maxDaysToScheduleAhead: restaurant.maxDaysToScheduleAhead?.toString() || '',
        defaultOrderClosingTime: restaurant.defaultOrderClosingTime || '',
        pickupTimeSlot: splitDeliverySlotsByMeal(restaurant.pickupTimeSlots || []),
        pickupTimeSlotAdmin: parseTimeSlots(restaurant.pickupTimeSlotsAdmin || []),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantData]);

  const handleInputChange = (section: string, field: string, value: string) => {
    if (
      section === 'deliveryBreakfast' ||
      section === 'deliveryLunch' ||
      section === 'deliveryDinner' ||
      section === 'pickupBreakfast' ||
      section === 'pickupLunch' ||
      section === 'pickupDinner'
    ) {
      const mealKey = section === 'deliveryBreakfast'
        ? 'breakfast'
        : section === 'deliveryLunch'
          ? 'lunch'
          : section === 'pickupBreakfast'
            ? 'breakfast'
            : section === 'pickupLunch'
              ? 'lunch'
              : 'dinner';

      const slotType = section.startsWith('pickup') ? 'pickupTimeSlot' : 'deliveryTimeSlot';

      setFormData({
        ...formData,
        [slotType]: {
          ...(formData[slotType as keyof Pick<OrderTimesData, 'deliveryTimeSlot' | 'pickupTimeSlot'>] as DeliveryMealTimeSlots),
          [mealKey]: {
            ...(formData[slotType as keyof Pick<OrderTimesData, 'deliveryTimeSlot' | 'pickupTimeSlot'>] as DeliveryMealTimeSlots)[mealKey],
            [field]: value,
          },
        },
      });
    } else if (section === 'deliveryTimeSlotAdmin' || section === 'pickupTimeSlotAdmin') {
      setFormData({
        ...formData,
        [section]: {
          ...(formData[section as keyof Pick<OrderTimesData, 'deliveryTimeSlotAdmin' | 'pickupTimeSlotAdmin'>] as TimeSlot),
          [field]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [field]: field === 'deliveryOrderDelayTime' ? parseInt(value) || 0 : value,
      });
    }
  };

  const handleClearSection = (section: string) => {
    const emptyTimeSlot: TimeSlot = { openTime: '', closeTime: '', timeRange: '' };
    const emptyMealTimeSlots: DeliveryMealTimeSlots = {
      breakfast: { openTime: '', closeTime: '', timeRange: '' },
      lunch:     { openTime: '', closeTime: '', timeRange: '' },
      dinner:    { openTime: '', closeTime: '', timeRange: '' },
    };

    switch (section) {
      case 'deliveryTimeSlot':
        setFormData({ ...formData, deliveryTimeSlot: emptyMealTimeSlots });
        break;
      case 'deliveryTimeSlotAdmin':
        setFormData({ ...formData, deliveryTimeSlotAdmin: emptyTimeSlot });
        break;
      case 'pickupTimeSlot':
        setFormData({ ...formData, pickupTimeSlot: emptyMealTimeSlots });
        break;
      case 'pickupTimeSlotAdmin':
        setFormData({ ...formData, pickupTimeSlotAdmin: emptyTimeSlot });
        break;
      default:
        break;
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Generate time slots arrays from form data
      const breakfastSlots = generateTimeSlots(
        formData.deliveryTimeSlot.breakfast.openTime,
        formData.deliveryTimeSlot.breakfast.closeTime,
        parseInt(formData.deliveryTimeSlot.breakfast.timeRange) || 30
      );

      const lunchSlots = generateTimeSlots(
        formData.deliveryTimeSlot.lunch.openTime,
        formData.deliveryTimeSlot.lunch.closeTime,
        parseInt(formData.deliveryTimeSlot.lunch.timeRange) || 30
      );

      const dinnerSlots = generateTimeSlots(
        formData.deliveryTimeSlot.dinner.openTime,
        formData.deliveryTimeSlot.dinner.closeTime,
        parseInt(formData.deliveryTimeSlot.dinner.timeRange) || 30
      );

      const deliveryTimeSlots = mergeDeliverySlots(breakfastSlots, lunchSlots, dinnerSlots);

      const deliveryTimeSlotsAdmin = generateTimeSlots(
        formData.deliveryTimeSlotAdmin.openTime,
        formData.deliveryTimeSlotAdmin.closeTime,
        parseInt(formData.deliveryTimeSlotAdmin.timeRange) || 30
      );

      const pickupBreakfastSlots = generateTimeSlots(
        formData.pickupTimeSlot.breakfast.openTime,
        formData.pickupTimeSlot.breakfast.closeTime,
        parseInt(formData.pickupTimeSlot.breakfast.timeRange) || 30
      );

      const pickupLunchSlots = generateTimeSlots(
        formData.pickupTimeSlot.lunch.openTime,
        formData.pickupTimeSlot.lunch.closeTime,
        parseInt(formData.pickupTimeSlot.lunch.timeRange) || 30
      );

      const pickupDinnerSlots = generateTimeSlots(
        formData.pickupTimeSlot.dinner.openTime,
        formData.pickupTimeSlot.dinner.closeTime,
        parseInt(formData.pickupTimeSlot.dinner.timeRange) || 30
      );

      const pickupTimeSlots = mergeDeliverySlots(
        pickupBreakfastSlots,
        pickupLunchSlots,
        pickupDinnerSlots
      );

      const pickupTimeSlotsAdmin = generateTimeSlots(
        formData.pickupTimeSlotAdmin.openTime,
        formData.pickupTimeSlotAdmin.closeTime,
        parseInt(formData.pickupTimeSlotAdmin.timeRange) || 30
      );

      const updateData = {
        deliveryTimeSlots,
        deliveryTimeSlotsAdmin,
        pickupOrderClosingTime: formData.pickUpOrderClosingTime,
        deliveryOrderDelayTime: formData.deliveryOrderDelayTime,
        maxDaysToScheduleAhead: parseInt(formData.maxDaysToScheduleAhead) || 0,
        defaultOrderClosingTime: formData.defaultOrderClosingTime,
        pickupTimeSlots,
        pickupTimeSlotsAdmin,
      };

      await updateRestaurant({
        restaurantId: selectedRestaurantId,
        data: updateData,
      }).unwrap();

      toast.success('Order times updated successfully');
      // triggerGetRestaurant(selectedRestaurantId);
    } catch (error) {
      console.error('Error updating order times:', error);
      toast.error('Failed to update order times');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <BreadCrumbs hasSubMenus={true} />
        <div className="py-4">
          <Card>
            <CardBody className="text-center">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p className="mt-3">Loading...</p>
            </CardBody>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div className="py-4">
        <Card>
          <CardHeader>
            <h5 className="mb-0">Order Times Configuration</h5>
          </CardHeader>
          <CardBody>
            <Form>
              {/* Delivery Time Slot */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 text-primary">Delivery Time Slots for Customers</h6>
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleClearSection('deliveryTimeSlot')}>
                    Clear
                  </button>
                </div>

                <h6 className="mb-2">01. Breakfast</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlot.breakfast.openTime}
                        onChange={(e) =>
                          handleInputChange('deliveryBreakfast', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlot.breakfast.closeTime}
                        onChange={(e) =>
                          handleInputChange('deliveryBreakfast', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.deliveryTimeSlot.breakfast.timeRange}
                        onChange={(e) =>
                          handleInputChange('deliveryBreakfast', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2">02. Lunch</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlot.lunch.openTime}
                        onChange={(e) =>
                          handleInputChange('deliveryLunch', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlot.lunch.closeTime}
                        onChange={(e) =>
                          handleInputChange('deliveryLunch', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.deliveryTimeSlot.lunch.timeRange}
                        onChange={(e) =>
                          handleInputChange('deliveryLunch', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2">03. Dinner</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlot.dinner.openTime}
                        onChange={(e) =>
                          handleInputChange('deliveryDinner', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlot.dinner.closeTime}
                        onChange={(e) =>
                          handleInputChange('deliveryDinner', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.deliveryTimeSlot.dinner.timeRange}
                        onChange={(e) =>
                          handleInputChange('deliveryDinner', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Delivery Time Slot Admin */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 text-primary">Delivery Time Slots for Admin</h6>
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleClearSection('deliveryTimeSlotAdmin')}>
                    Clear
                  </button>
                </div>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlotAdmin.openTime}
                        onChange={(e) =>
                          handleInputChange('deliveryTimeSlotAdmin', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.deliveryTimeSlotAdmin.closeTime}
                        onChange={(e) =>
                          handleInputChange('deliveryTimeSlotAdmin', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.deliveryTimeSlotAdmin.timeRange}
                        onChange={(e) =>
                          handleInputChange('deliveryTimeSlotAdmin', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Pickup Time Slot Admin */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 text-primary">Pickup Time Slots for Customers</h6>
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleClearSection('pickupTimeSlot')}>
                    Clear
                  </button>
                </div>

                <h6 className="mb-2">01. Breakfast</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlot.breakfast.openTime}
                        onChange={(e) =>
                          handleInputChange('pickupBreakfast', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlot.breakfast.closeTime}
                        onChange={(e) =>
                          handleInputChange('pickupBreakfast', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.pickupTimeSlot.breakfast.timeRange}
                        onChange={(e) =>
                          handleInputChange('pickupBreakfast', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2">02. Lunch</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlot.lunch.openTime}
                        onChange={(e) =>
                          handleInputChange('pickupLunch', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlot.lunch.closeTime}
                        onChange={(e) =>
                          handleInputChange('pickupLunch', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.pickupTimeSlot.lunch.timeRange}
                        onChange={(e) =>
                          handleInputChange('pickupLunch', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2">03. Dinner</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlot.dinner.openTime}
                        onChange={(e) =>
                          handleInputChange('pickupDinner', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlot.dinner.closeTime}
                        onChange={(e) =>
                          handleInputChange('pickupDinner', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.pickupTimeSlot.dinner.timeRange}
                        onChange={(e) =>
                          handleInputChange('pickupDinner', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Pickup Time Slot Admin */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 text-primary">Pickup Time Slots for Admin</h6>
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleClearSection('pickupTimeSlotAdmin')}>
                    Clear
                  </button>
                </div>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlotAdmin.openTime}
                        onChange={(e) =>
                          handleInputChange('pickupTimeSlotAdmin', 'openTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickupTimeSlotAdmin.closeTime}
                        onChange={(e) =>
                          handleInputChange('pickupTimeSlotAdmin', 'closeTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Time Range (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.pickupTimeSlotAdmin.timeRange}
                        onChange={(e) =>
                          handleInputChange('pickupTimeSlotAdmin', 'timeRange', e.target.value)
                        }
                        placeholder="e.g., 30"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Pick Up Order */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-primary">Pickup Order Closing Time</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Closing Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.pickUpOrderClosingTime}
                        onChange={(e) =>
                          handleInputChange('', 'pickUpOrderClosingTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-primary">Default Order Closing Time</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Closing Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={formData.defaultOrderClosingTime}
                        onChange={(e) =>
                          handleInputChange('', 'defaultOrderClosingTime', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Delivery Order Delay Time */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-primary">Delivery Order Delay Time</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Delivery Order Delay Time (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.deliveryOrderDelayTime}
                        onChange={(e) =>
                          handleInputChange('', 'deliveryOrderDelayTime', e.target.value)
                        }
                        placeholder="e.g., 15"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Max Days to Schedule Ahead */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-primary">Schedule Ahead</h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Max Days to Schedule Ahead</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.maxDaysToScheduleAhead}
                        onChange={(e) =>
                          handleInputChange('', 'maxDaysToScheduleAhead', e.target.value)
                        }
                        placeholder="e.g., 7"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Save Button */}
              <div className="d-flex justify-content-end mt-4">
                {isSaving ? (
                  <ButtonComponent
                    titel="Saving..."
                    isDisabled={true}
                    icon={<FontAwesomeIcon icon={faSpinner} spin />}
                  />
                ) : (
                  <ButtonComponent
                    titel="Save"
                    onClick={handleSave}
                    icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                  />
                )}
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default OrderTimes;